var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useState } from 'react';
import { customEvent, EventError, EventType } from '@assets/utils/customEvent';
import useLocalStorage from '@embed/hooks/useLocalStorage';
import { fetcher } from '@embed/store/fetchers';
import { isMobileWeb } from '@embed/store/tools/utils';
const getAdConfigFromStorage = (fromStorage) => {
    if (!fromStorage || !fromStorage.expire) {
        return undefined;
    }
    const TOO_OLD = 30 * 60 * 1000; //30mins
    if (Date.now() > fromStorage.expire + TOO_OLD) {
        return undefined;
    }
    return fromStorage.config;
};
const getStorageKey = (params, appContext) => {
    switch (true) {
        case !!params.playlist:
            return `_fwn_spcfgpl_${params.playlist}`;
        case !!params.channel:
            return `_fwn_spcfgchnl_${params.channel}`;
        case !!params.app_id:
            return `_fwn_spcfgpc_${params.app_id}`;
        case !!(appContext === null || appContext === void 0 ? void 0 : appContext.oauthClientId):
            return `_fwn_spcfgoauthcid_${appContext === null || appContext === void 0 ? void 0 : appContext.oauthClientId}`;
        default:
            return '';
    }
};
export const useAdConfigStorage = (params, appContext) => {
    const isDesktopMode = !isMobileWeb();
    const storageKey = getStorageKey(params, appContext);
    const [adConfigStorage, setAdConfigStorage] = useLocalStorage(storageKey, undefined);
    const [adConfig, setAdConfig] = useState(getAdConfigFromStorage(adConfigStorage));
    const handleError = useCallback(() => {
        // in case of error we store null for shorter time to prevent calling server all the time
        const ttl = 60 * 1000;
        setAdConfigStorage({
            expire: new Date().getTime() + ttl,
            config: null,
        });
        setAdConfig(null);
        params.target &&
            customEvent({
                target: params.target,
                type: [EventType.FW_ERROR, EventType.FW_EMBEDFEED_ERROR],
                data: {
                    detail: EventError.API_ERROR,
                },
            });
    }, []);
    const isExpired = (adConfigStorage === null || adConfigStorage === void 0 ? void 0 : adConfigStorage.expire) && Date.now() > (adConfigStorage === null || adConfigStorage === void 0 ? void 0 : adConfigStorage.expire);
    useEffect(() => {
        ;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            if ((typeof adConfig === 'undefined' || isExpired) && storageKey) {
                try {
                    const query = {};
                    if (params === null || params === void 0 ? void 0 : params.playlist) {
                        query['playlist_id'] = params.playlist;
                    }
                    else if (params === null || params === void 0 ? void 0 : params.channel) {
                        query['channel'] = params === null || params === void 0 ? void 0 : params.channel;
                    }
                    else if (params === null || params === void 0 ? void 0 : params.app_id) {
                        query['publisher_client_uid'] = params.app_id;
                    }
                    else if (appContext === null || appContext === void 0 ? void 0 : appContext.oauthClientId) {
                        query['oauth_app_uid'] = appContext.oauthClientId;
                    }
                    query['ad_target_platform'] = isDesktopMode
                        ? 'desktop_web'
                        : 'mobile_web';
                    const response = yield fetcher({
                        url: `${params === null || params === void 0 ? void 0 : params.api_host_ads}/api/ad_setting`,
                        query,
                    });
                    const adConfigResponse = response.status === 200 ? yield response.json() : null;
                    // GET /api/ad_setting can return 200 with error, ie. {error: 'playlist not found'}
                    if (!adConfigResponse || adConfigResponse.error) {
                        throw new Error('api');
                    }
                    if (!adConfig) {
                        setAdConfig(adConfigResponse);
                    }
                    const ttl = 5 * 60 * 1000; //5min
                    setAdConfigStorage({
                        expire: new Date().getTime() + ttl,
                        config: adConfigResponse,
                    });
                }
                catch (_a) {
                    handleError();
                }
            }
        }))();
    }, [
        params.channel,
        params.playlist,
        params.app_id,
        appContext === null || appContext === void 0 ? void 0 : appContext.oauthClientId,
        adConfig,
        isExpired,
        storageKey,
        setAdConfigStorage,
        setAdConfig,
        handleError,
    ]);
    return adConfig;
};
